import { useEffect, useState } from "react";
import { readFilterMemory, writeFilterMemory, } from "../../infrastructure/filter-memory-storage";
function hydrate(screenKey, initialValue, revalidate) {
    const stored = readFilterMemory(screenKey);
    if (stored === null || stored === undefined)
        return initialValue;
    try {
        // Spread the defaults underneath so a filter added after the user's session
        // started (or removed from the screen) can't produce an undefined field.
        const merged = typeof stored === "object" && !Array.isArray(stored)
            ? Object.assign(Object.assign({}, initialValue), stored)
            : stored;
        return revalidate ? revalidate(merged) : merged;
    }
    catch (_a) {
        return initialValue;
    }
}
// One object per screen — the normal case. All of a screen's filters live in a
// single entry, so a restore is a single synchronous read before first render and
// therefore triggers exactly one data request rather than one per filter.
export function useSessionFilterMemory(screenKey, initialValue, revalidate) {
    // Hydration sits inside the useState initializer so it runs exactly once —
    // reading storage (and running `revalidate`) on every render would be wasteful
    // and would re-validate values the user has since chosen deliberately.
    const [value, setValue] = useState(() => hydrate(screenKey, initialValue, revalidate));
    useEffect(() => {
        writeFilterMemory(screenKey, value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return [value, setValue];
}
// One value under its own key — for screens whose filter state is spread across
// many independent useState calls (the KPI dashboard's per-card granularity and
// period pickers). Each value keeps its own entry, so there is no lost-update race
// between hooks and the call sites keep their existing setter shape.
export function useSessionFilterValue(screenKey, field, initialValue, revalidate) {
    const key = `${screenKey}.${field}`;
    const [value, setValue] = useState(() => hydrate(key, typeof initialValue === "function"
        ? initialValue()
        : initialValue, revalidate));
    useEffect(() => {
        writeFilterMemory(key, value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return [value, setValue];
}
// Adapts a single field of a filters object to the `Dispatch<SetStateAction<V>>`
// shape existing filter components already expect, so persisting a screen's
// filters doesn't force a rewrite of its header component. `extra` applies
// alongside the field — used to reset paging when a filter changes, exactly where
// the screen already did so.
export function filterFieldSetter(setFilters, key, extra) {
    return (action) => setFilters((prev) => {
        const next = typeof action === "function"
            ? action(prev[key])
            : action;
        return Object.assign(Object.assign(Object.assign({}, prev), { [key]: next }), extra);
    });
}
